import { useEffect, useRef, useState } from 'react';
import pageService from '../../services/archive/archive.service';
import UrlsUtil from '../../utils/url.util';

type ArchivePersistence = {
  filter: string,
  rawData: any[],
  summaries: any[],
  lastUpdated: string | undefined,
};

function Archive() {

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [persistence, setPersistence] = useState<ArchivePersistence>({
    filter: '',
    rawData: [],
    summaries: [],
    lastUpdated: 'loading -- please wait...',
  });

  const ws = useRef(null);

  const clearFilter = () => {
    filterData(undefined);
    const filter = document.getElementById('filter') as HTMLInputElement;
    filter.value = '';
  };

  const executeFilterData = async (filter?: string, toDisplay?: any[]): Promise<any[]> => {
    if (!toDisplay) {
      toDisplay = persistence.rawData;
    }

    if (filter) {
      const filterLowered = filter.toLowerCase();
      toDisplay = toDisplay.filter((item: any) => {
        return item.content?.toLowerCase().indexOf(filterLowered) > -1
          || item.customerId?.toLowerCase().indexOf(filterLowered) > -1
          || item.description?.toLowerCase().indexOf(filterLowered) > -1
          || item.entityType.toLowerCase().indexOf(filterLowered) > -1
          || item.flowKey.toLowerCase().indexOf(filterLowered) > -1
          || item.key.toLowerCase().indexOf(filterLowered) > -1
          || item.name?.toLowerCase().indexOf(filterLowered) > -1
          || item.currentStateDisplay?.toLowerCase().indexOf(filterLowered) > -1;
      });
    }

    return toDisplay;
  };

  const filterData = async (e: any) => {
    let value = e?.target?.value || '';
    setPersistence(current => ({ ...current, 'filter': value }));
    const toDisplay = await executeFilterData(value);
    setPersistenceProperty('summaries', toDisplay);
  };

  const forceRefresh = async () => {
    await getData(persistence.filter);
  };

  const getData = async (filter?: string) => {
    if (!isFetching) {
      setPersistenceProperty('lastUpdated', `querying data...`);
      setIsFetching(true);
      await pageService.getAll()
        .then(async (response: any) => {
          const now = new Date();

          if (response.status === 200 && response.data) {

            response.data.sort((a: any, b: any) => {
              var aDate = new Date(a.created);
              var bDate = new Date(b.created);
              if (aDate > bDate) return -1;
              if (aDate < bDate) return 1;
              return 0;
            });

            const toDisplay = await executeFilterData(filter, response.data);

            setPersistence(current => ({
              ...current,
              'lastUpdated': `last update: ${now.getHours()}:${`0${now.getMinutes()}`.slice(-2)}:${`0${now.getSeconds()}`.slice(-2)}`,
              'rawData': response.data,
              'summaries': toDisplay
            }));
          }
        })
        .finally(() => setIsFetching(false));
    }
  };

  const handleKeyUp = (e: any) => {
    const key = (e.key || e.keyCode).toString().toLowerCase();
    if ((key === '8' || key === 'backspace') && persistence.filter.length === 0) {
      filterData(undefined);
    }
  };

  const highlightElement = (pattern: RegExp, prop: any): string => {
    return persistence.filter
      ? prop?.replace(pattern, `<span class='filter-highlighted'>$&</span>`) || prop
      : prop;
  }

  const setPersistenceProperty = (name: string, value: any) => {
    setPersistence(current => {
      const updated = { ...current, [name]: value };
      return updated;
    });
  };

  useEffect(() => {
    // @ts-ignore
    ws.current = new WebSocket(UrlsUtil.determineWebSocketUrl(window));

    // @ts-ignore
    ws.current.onopen = (event) => {
      console.log('archive: web socket opened.', event);
    };

    // @ts-ignore
    ws.current.onclose = (cls) => {
      console.log('archive: ws closed', cls);
    };

    // @ts-ignore
    ws.current.onerror = (err) => {
      console.log('archive: ws Error', err);
    };

    const wsCurrent = ws.current;
    return () => {
      // @ts-ignore
      wsCurrent?.close();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tokenCheck = localStorage.getItem('token');
    if (!tokenCheck) {
      window.location.href = "/";
    }

    if (!isInitialized) {
      setIsInitialized(true);

      // @ts-ignore
      ws.current.onmessage = async (msg) => {
        console.log('archive: incoming message', msg);

        setTimeout(async () => {
          await getData(persistence.filter);
        }, 1000);
      };
      
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, persistence]);

  return (
    <div className='data-display'>
      <div className="row" style={{ width: '100%', textAlign: 'left', marginBottom: '0.5em' }}>
        <div className='col-12'>
          <h1>Journal Archive</h1>
          <div>
            Showing {persistence.summaries?.length || 0} of {persistence.rawData?.length || 0} &bull; <span style={{ marginTop: '0em' }}>{persistence.lastUpdated}</span>
            {
              persistence.summaries?.length > 0 &&
              <button className='btn btn-success header-refresh' onClick={() => forceRefresh()}>Force Refresh</button>
            }
          </div>
        </div>
      </div>
      {
        isInitialized && persistence.lastUpdated && (persistence.lastUpdated.startsWith('last update: ') || persistence.lastUpdated.startsWith('querying data')) &&
        <div className="row" style={{ width: '100%', textAlign: 'left', marginBottom: '0.5em' }}>
          <div className='col-12'>
            Filter: <input type="text" id="filter" onInput={(e) => filterData(e)} onKeyUp={(e) => handleKeyUp(e)} />
            {
              persistence.filter &&
              <span><button className='btn btn-link filter-clear' onClick={() => clearFilter()}>x</button></span>
            }
          </div>
        </div>
      }
      <table className='table table-striped table-hover'>
        <tbody>
          {
            persistence.filter && persistence.summaries.length === 0
              ? <tr><td><strong>No matching records to display</strong></td></tr>
              : persistence.summaries.map((summary: any, i) => {
                const pattern = new RegExp(persistence.filter, 'ig');
                const updatedContent = highlightElement(pattern, summary.content);
                const updatedCustomerId = highlightElement(pattern, summary.customerId);
                const updatedDescription = highlightElement(pattern, summary.description);
                const updatedEntityType = highlightElement(pattern, summary.entityType);
                const updatedFlowKey = highlightElement(pattern, summary.flowKey);
                const updatedKey = highlightElement(pattern, summary.key);
                const updatedName = highlightElement(pattern, summary.name);
                const updatedCurrentStateDisplay = highlightElement(pattern, summary.currentStateDisplay);

                return <tr key={i}>
                  <td>
                    <div className='row'>
                      <div className='col-12'>
                        <h2 dangerouslySetInnerHTML={{ __html: updatedName }}></h2>
                        <div className='row'>
                          <div className='col-4 row-left-panel'>
                            <p>
                              <strong>Description:</strong> <span dangerouslySetInnerHTML={{ __html: updatedDescription }}></span><br />
                              <strong>Created:</strong> {new Date(summary.created).toString()}<br />
                              <strong>Customer ID: <span dangerouslySetInnerHTML={{ __html: updatedCustomerId }}></span></strong><br />
                              <strong>Key:</strong> <span dangerouslySetInnerHTML={{ __html: updatedKey }}></span>
                            </p>
                          </div>
                          <div className='col-8 row-right-panel'>
                            <p>
                              <strong>Flow {summary.isActive ? 'is ACTIVE' : 'DEACTIVATED'}</strong><br />
                              <strong>State:</strong> <span dangerouslySetInnerHTML={{ __html: updatedCurrentStateDisplay }}></span><br />
                              <strong>Flow Key:</strong> <span dangerouslySetInnerHTML={{ __html: updatedFlowKey }}></span><br />
                              <strong>Entity Type: <span dangerouslySetInnerHTML={{ __html: updatedEntityType }}></span></strong><br />
                              <strong>Content:</strong> <span dangerouslySetInnerHTML={{ __html: updatedContent }}></span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              })
          }
        </tbody>
      </table>
    </div>
  );
};

export default Archive;